import { memo } from 'react';

import { UsersRoundIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Skeleton } from '@/components/ui/skeleton';
import { useConversationContext } from '@/hooks/useConversationContext';
import { useContact } from '@/modules/contacts/hooks/useContact';
import { useContactMemories } from '@/modules/contacts/hooks/useContactMemories';
import { selectAccountSlug } from '@/redux/session/selectors';

import { ProfileMemoryCard } from './ProfileMemoryCard';
import { ProfileSidebar } from './ProfileSidebar';

const MemoriesSection = ({ contactId }: { contactId: string }) => {
  const { t } = useTranslation();
  const { memories, status } = useContactMemories(contactId);

  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-foreground">{t('contacts.memories')}</h3>

      {status === 'pending' && <Skeleton className="h-16 w-full" />}

      {status !== 'pending' && memories.length === 0 && (
        <p className="text-sm text-foreground-muted">{t('contacts.no_memories')}</p>
      )}

      {memories.length > 0 && (
        <div className="flex flex-col gap-3">
          {memories.map((memory) => (
            <ProfileMemoryCard key={memory.memory_id} memory={memory} />
          ))}
        </div>
      )}
    </div>
  );
};

export const ContactProfilePanel = memo(() => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { isLoading, user } = useConversationContext();
  const contactId = user?.contact_id;
  const { contact, status } = useContact(contactId);

  const renderContent = () => {
    // The conversation context carries the contact_id, so wait for it before
    // deciding whether a contact is linked.
    if (isLoading || (contactId && status === 'pending')) {
      return <Skeleton className="h-96 w-full" />;
    }

    if (!contactId) {
      return (
        <div className="rounded-lg border border-dashed border-stroke p-5 text-center">
          <UsersRoundIcon size={20} className="mx-auto mb-2 text-foreground-muted" />
          <p className="text-sm text-foreground-muted">{t('contacts.no_linked_contact')}</p>
        </div>
      );
    }

    if (status === 'error') {
      return <p className="text-sm text-foreground-muted">{t('contacts.not_found')}</p>;
    }

    if (!contact) {
      return null;
    }

    return (
      <ProfileSidebar
        contact={contact}
        bordered={false}
        nameLinkTo={slug ? `/${slug}/contacts/${contact.contact_id}` : undefined}
        footer={<MemoriesSection contactId={contact.contact_id} />}
      />
    );
  };

  return <div className="p-2">{renderContent()}</div>;
});

ContactProfilePanel.displayName = 'ContactProfilePanel';
